import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import classNames from 'classnames';
import is from 'is_js';
import PropTypes from 'prop-types';
import { Collapse, Nav, Navbar } from 'reactstrap';
// import bgNavbarImg from '../../assets/img/generic/bg-navbar.png';
import { navbarBreakPoint } from '../../config';
import AppContext from '../../context/Context';
import routes from '../../routes';
import routesIncompleteMember, { initialisedUserRoutes, homeRoutes } from '../../routesIncompleteMember';
import routesAdmin from '../../routesAdmin';
import Flex from '../common/Flex';
import Logo from './Logo';
import NavbarVerticalMenu from './NavbarVerticalMenu';
import ToggleButton from './ToggleButton';
import Loader from '../common/Loader';

import getFirebase from '../../services/firebase';
import { APPLICATION_STATUS } from 'Types/constants';
const firebase = getFirebase();
const db = getFirebase().firestore();

const NavbarVertical = ({ navbarStyle }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [level, setLevel] = useState('');
  const [loading, setLoading] = useState(true);
  const [applicationFormType, setApplicationFormType] = useState('');
  const [userId, setUserId] = useState('');

  const navBarRef = useRef(null);

  const { showBurgerMenu, isNavbarVerticalCollapsed, setIsNavbarVerticalCollapsed } = useContext(AppContext);

  const HTMLClassList = document.getElementsByTagName('html')[0].classList;
  //Control Component did mount and unmounted of hover effect
  if (isNavbarVerticalCollapsed) {
    HTMLClassList.add('navbar-vertical-collapsed');
  }

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(user => {
      if (user && user.uid) {
        setUserId(user.uid);
        db.collection('authority')
          .doc(user.uid)
          .get()
          .then(doc => {
            if (doc.exists && doc.data().level) {
              setLevel(doc.data().level);
              setApplicationFormType(doc.data()?.applicationFormType || '');
            } else if (!doc.exists) {
              setLevel(APPLICATION_STATUS.INCOMPLETE_MEMBER);
              // use the authenticated user's uid when creating a new authority doc
              db.collection('authority')
                .doc(user.uid)
                .set({ level: APPLICATION_STATUS.INCOMPLETE_MEMBER })
                .catch(error => {
                  console.error('Error setting level', error);
                });
            }

            if (doc.exists && doc.data().level === 'ADMIN') {
              setIsAdmin(true);
            } else {
              setIsAdmin(false);
            }
          })
          .then(() => {
            setLoading(false);
          })
          .catch(error => {
            console.error('Error getting document:', error);
          });
      } else {
        setIsAdmin(false);
      }
    });

    // Cleanup function to unsubscribe when component unmounts
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (is.windows()) {
      HTMLClassList.add('windows');
    }
    if (is.chrome()) {
      HTMLClassList.add('chrome');
    }
    if (is.firefox()) {
      HTMLClassList.add('firefox');
    }
    return () => {
      HTMLClassList.remove('navbar-vertical-collapsed-hover');
    };
  }, [isNavbarVerticalCollapsed, HTMLClassList]);

  // Helper: clone routes and inject userId into any path placeholders like :uid or {uid}
  const injectUidIntoPath = (path, uid) => {
    if (typeof path !== 'string') return path;
    const id = uid || '';
    return path.replace(/:uid|\{uid\}|\{userId\}|:userId/g, id);
  };

  const cloneAndInject = (items, uid) => {
    if (!Array.isArray(items)) return [];
    return items
      .map(item => {
        if (!item || typeof item !== 'object') return null;
        const cloned = { ...item };
        if (cloned.path) cloned.path = injectUidIntoPath(cloned.path, uid);
        if (cloned.to) cloned.to = injectUidIntoPath(cloned.to, uid);
        if (Array.isArray(cloned.children)) cloned.children = cloneAndInject(cloned.children, uid);
        return cloned;
      })
      .filter(Boolean)
      .filter(r => r.path || r.to || (Array.isArray(r.children) && r.children.length));
  };

  const selectedRoutes = useMemo(() => {
    let base;
    if (isAdmin) base = routesAdmin;
    else if (
      (level === APPLICATION_STATUS.INCOMPLETE_MEMBER ||
        level === 'PENDING_APPROVAL_MEMBER' ||
        level === 'PENDING_VERIFICATION_MEMBER') &&
      (applicationFormType === 'new' || applicationFormType === 'existing')
    )
      base = Array.isArray(initialisedUserRoutes)
        ? initialisedUserRoutes
        : [homeRoutes, initialisedUserRoutes(userId, applicationFormType)];
    else if (level === APPLICATION_STATUS.INCOMPLETE_MEMBER || level === 'PENDING_VERIFICATION_MEMBER')
      base = routesIncompleteMember;
    else base = routes;

    return cloneAndInject(base, userId);
  }, [isAdmin, level, userId, applicationFormType]);

  //Control mouseEnter event
  const hoverTimerRef = useRef(null);
  const handleMouseEnter = () => {
    if (isNavbarVerticalCollapsed) {
      hoverTimerRef.current = window.setTimeout(() => {
        HTMLClassList.add('navbar-vertical-collapsed-hover');
      }, 100);
    }
  };
  return (
    <Navbar
      expand={navbarBreakPoint}
      className={classNames('navbar-vertical navbar-glass', {
        [`navbar-${navbarStyle}`]: navbarStyle !== 'transparent'
      })}
      light
    >
      <Flex align="center">
        <ToggleButton
          isNavbarVerticalCollapsed={isNavbarVerticalCollapsed}
          setIsNavbarVerticalCollapsed={setIsNavbarVerticalCollapsed}
        />
        {/* Render the logo inside the vertical navbar so the brand is always visible */}
        <div className="d-flex align-items-center ml-2">
          <Logo at="navbar-vertical" width={'100%'} />
        </div>
      </Flex>

      <Collapse
        navbar
        isOpen={showBurgerMenu}
        className="scrollbar"
        innerRef={navBarRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => {
          clearTimeout(hoverTimerRef.current);
          HTMLClassList.remove('navbar-vertical-collapsed-hover');
        }}
        style={
          navbarStyle === 'card'
          // && {
          //   backgroundImage: `linear-gradient(-45deg, rgba(0, 160, 255, 0.86), #0048a2),url(${bgNavbarImg})`
          // }
        }
      >
        {loading ? (
          <Loader />
        ) : (
          <Nav navbar vertical>
            <NavbarVerticalMenu routes={selectedRoutes} />
          </Nav>
        )}
        {/* <div className="settings px-3 px-xl-0">
          {isCombo && (
            <div className={`d-${topNavbarBreakpoint}-none`}>
              <div className="navbar-vertical-divider">
                <hr className="navbar-vertical-hr my-2" />
              </div>
              <Nav navbar>
                <NavbarTopDropDownMenus setNavbarCollapsed={setNavbarCollapsed} setShowBurgerMenu={setShowBurgerMenu} />
              </Nav>
            </div>
          )}
          <div className="navbar-vertical-divider">
            <hr className="navbar-vertical-hr my-2" />
          </div>
        </div> */}
      </Collapse>
    </Navbar>
  );
};

NavbarVertical.propTypes = {
  navbarStyle: PropTypes.string
};

NavbarVertical.defaultProps = {
  navbarStyle: 'transparent'
};

export default NavbarVertical;
